import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import InviteLeadsForm from './components/InviteLeadsForm';
import ListingContext from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/providers/Listing/Listing.context';
import {
  getArrayLength,
  getCheckedValuesInObject,
  getIsArrayEmpty,
} from 'utils';
import api from 'api';
import {
  ENQUIRY_ORIGIN,
  EnquiryCreationInitiatedEventProps,
  MANUAL_ENQUIRY_SUBCATEGORY,
} from 'models/MixpanelEvents';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { InviteLeadsFormValues } from './components/InviteLeadsForm/InviteLeadsForm.component';

import './InviteLeadsFromPreviousDeals.styles.scss';

type InviteLeadsFromPreviousDealsProps = {
  className?: string;
} & ModalProps;

const InviteLeadsFromPreviousDeals: React.ForwardRefRenderFunction<
  ModalRef,
  InviteLeadsFromPreviousDealsProps
> = (props, ref) => {
  const { className, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const {
    listing: { _id: listingId },
    dealRoom: { _id: dealRoomId, name, leadStagesCopies },
    setEnquiry,
  } = useContext(ListingContext);

  const { popSuccess } = useContext(PopNotificationsContext);

  const getMixpanelProps = useGetMixpanelProps();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('invite-leads-from-previous-deals', className);

  const { t } = useTranslation();

  const onClose = useCallback(() => modal?.close(), [modal]);

  const onSubmit = useCallback(
    async (values: InviteLeadsFormValues) => {
      const { leads, includeMessage, customMessage } = values;

      const buyerIds = getCheckedValuesInObject(leads);

      if (getIsArrayEmpty(buyerIds)) return;

      buyerIds.forEach((_) => {
        mixpanelService.enquiryCreationInitiated(
          getMixpanelProps({
            dealId: dealRoomId,
            dealName: name,
            enquiryOrigin: ENQUIRY_ORIGIN.MANUAL,
            manualEnquirySubcategory: MANUAL_ENQUIRY_SUBCATEGORY.PREVIOUS_LEAD,
          }) as EnquiryCreationInitiatedEventProps,
        );
      });

      const { data } = await api.portfolio.seller.addPreviousLead(listingId, {
        buyerIds,
        ...(includeMessage && customMessage && { customMessage }),
      });

      popSuccess(
        t('successfullyInvitedLeads', { count: getArrayLength(buyerIds) }),
      );
      setEnquiry(data);
      onClose();
    },
    [
      dealRoomId,
      listingId,
      name,
      onClose,
      popSuccess,
      setEnquiry,
      getMixpanelProps,
      t,
    ],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('inviteLeadsFromDeals')}
      {...rest}
    >
      <InviteLeadsForm
        description={t('inviteLeadsFromDealsDescription', {
          dealRoomName: name,
        })}
        submitText={t('addEnquiries')}
        dealRoomId={dealRoomId}
        leadStagesCopies={leadStagesCopies}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Modal>
  );
};

export default forwardRef(InviteLeadsFromPreviousDeals);
